import { styled, alpha } from '@mui/material/styles';
import { Chip } from '@mui/material';
import { StatusDensity, StatusType, StatusVisualStyle } from './StatusChip.types';

const statusPalette: Record<StatusType, { base: string; text: string }> = {
  // Paleta pastel con contraste reforzado para tablas densas
  // Verde césped con contraste legible pero más claro en texto
  success: { base: '#22C55E', text: '#2FA66A' },
  warning: { base: '#F59E0B', text: '#C7771A' },
  error: { base: '#F05252', text: '#C24137' },
  info: { base: '#3B82F6', text: '#3C70E0' },
  default: { base: '#94A3B8', text: '#5B6475' },
  // Acento de "acción requerida" (índigo), distinto del azul informativo
  action: { base: '#6366F1', text: '#4F46E5' },
};

/**
 * StatusChip estilizado con un look más elevado y moderno.
 * - Borde fino + gradiente suave para volumen
 * - Sombra sutil para jerarquía
 * - Hover/focus con realce controlado
 */
export const StyledStatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'status' && prop !== 'visualStyle' && prop !== 'density',
})<{
  status: StatusType;
  visualStyle: StatusVisualStyle;
  density: StatusDensity;
}>(({ theme, status, visualStyle, density }) => {
  const palette = statusPalette[status] ?? statusPalette.default;
  const isOutline = visualStyle === 'outline';
  const isQuiet = visualStyle === 'quiet';
  const isCompact = density === 'compact';

  const baseBg = alpha(palette.base, isOutline ? 0.14 : isQuiet ? 0.16 : 0.22);
  const gradient =
    isOutline || isQuiet
      ? 'none'
      : `linear-gradient(135deg, ${alpha(palette.base, 0.32)} 0%, ${alpha(
          palette.base,
          0.18,
        )} 100%)`;
  const shadowColor = alpha(palette.base, isOutline || isQuiet ? 0.14 : 0.24);
  const height = isCompact ? 24 : 28;
  const minWidth = isCompact ? 72 : 82;
  const paddingInline = isCompact ? theme.spacing(0.25) : theme.spacing(0.5);
  const labelPadding = isCompact ? theme.spacing(1.25) : theme.spacing(1.75);

  return {
    fontWeight: 600,
    fontSize: '0.85rem',
    borderRadius: theme.spacing(2.75),
    height,
    minWidth,
    textAlign: 'center',
    letterSpacing: 0.3,
    paddingInline,
    backgroundColor: baseBg,
    backgroundImage: gradient,
    color: palette.text,
    border: 'none',
    boxShadow: isQuiet
      ? 'none'
      : isOutline
        ? `0 1px 3px ${shadowColor}`
        : `0 6px 18px ${shadowColor}, inset 0 1px 0 ${alpha(palette.base, 0.35)}`,
    transition: 'all 140ms ease-out',
    '& .MuiChip-label': {
      paddingLeft: labelPadding,
      paddingRight: labelPadding,
    },
    '& .MuiChip-icon': {
      color: palette.text,
      opacity: 1,
      marginLeft: theme.spacing(0.75),
    },
    '&:hover': {
      boxShadow: isQuiet
        ? 'none'
        : isOutline
          ? `0 2px 6px ${alpha(palette.base, 0.18)}`
          : `0 6px 16px ${alpha(palette.base, 0.22)}`,
      backgroundColor: alpha(palette.base, isOutline ? 0.1 : isQuiet ? 0.18 : 0.2),
    },
    '&:focus-visible': {
      outline: `2px solid ${alpha(palette.base, 0.35)}`,
      outlineOffset: 1,
    },
    '&.MuiChip-root': {
      backgroundColor: baseBg,
      backgroundImage: gradient,
      color: palette.text,
    },
  };
});
